import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosApi from "src/app/config";

const initialState = {
  userDetails: {},
  userOrganizations: [],
  isLoading: false,
  isError: false,
  isSuccess: false,
  error: null,

  isAddUserToOrganizationLoading: false,
  isAddUserToOrganizationError: false,
  isAddUserToOrganizationSuccess: false,
  addUserToOrganizationError: null,

  isUserOrganizationsLoading: false,
  isUserOrganizationsError: false,
  userOrganizationsError: null,
};

export const getUserDetails = createAsyncThunk(
  "user/fetchUserDetails",
  async ({ token }, { dispatch, getState, rejectWithValue, fulfillWithValue }) => {
    try {
      // Fetch user profile details from proper user API
      const userResponse = await axiosApi({ token }).get(`/user/`);
      const userDetails = userResponse?.data?.data || {};
      
      return fulfillWithValue({
        userDetails: userDetails,
      });
    } catch (error) {
      console.error('Error fetching user details:', error.response?.data || error.message);
      return rejectWithValue(error?.response?.data?.message || error.message);
    }
  }
);

export const addUserToOrganization = createAsyncThunk(
  "user/addUserToOrganization",
  async ({ token, formData, organizationId }, { dispatch, getState, rejectWithValue, fulfillWithValue }) => {
    try {
      const response = await axiosApi({ token, organizationId }).post(
        `/user/addUserToOrganization`,
        formData
      );
      return fulfillWithValue(JSON.parse(JSON.stringify(response.data)));
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateUserDetails = createAsyncThunk(
  "user/updateUserDetails",
  async ({ token, userData }, { dispatch, getState, rejectWithValue, fulfillWithValue }) => {
    try {
      const response = await axiosApi({ token }).post(
        `/user/updateUserDetails`,
        userData
      );
      return fulfillWithValue(response.data);
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const getUserOrganizations = createAsyncThunk(
  "user/getUserOrganizations",
  async ({ token, userId }, { dispatch, getState, rejectWithValue, fulfillWithValue }) => {
    try {
      // Using the public route for now as it has the getUserOrganizations implementation
      const response = await axiosApi({ token }).get(`/user/public/organizations?userId=${userId}`);
      return fulfillWithValue(response.data?.data || []);
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    clearSuccess: (state) => {
      state.isSuccess = false;
      state.isError = false;
      state.isLoading = false;
    },
    // Legacy action - no longer needed with simplified organization selection
    updateSelectedUser: (state, payload) => {
      // This action is deprecated - organization selection is now handled by organization slice
      console.warn('updateSelectedUser is deprecated - use organization slice instead');
    },
    
    clearState: (state) => {
      // Reset user state to initial state (useful when switching organizations)
      return { ...initialState };
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getUserDetails.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getUserDetails.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;

      const { userDetails } = action.payload ?? {};
      
      // Store user details 
      state.userDetails = userDetails;
    });
    builder.addCase(getUserDetails.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload.error;
      state.isError = true;
    });

    builder.addCase(addUserToOrganization.pending, (state) => {
      state.isAddUserToOrganizationLoading = true;
    });
    builder.addCase(addUserToOrganization.fulfilled, (state, action) => {
      state.isAddUserToOrganizationLoading = false;
      state.isAddUserToOrganizationSuccess = true;
    });
    builder.addCase(addUserToOrganization.rejected, (state, action) => {
      state.isAddUserToOrganizationLoading = false;
      state.addUserToOrganizationError = action.payload.error;
      state.isAddUserToOrganizationError = true;
    });

    // updateUserDetails reducers
    builder.addCase(updateUserDetails.pending, (state) => {
      state.isLoading = true;
      state.isError = false;
    });
    builder.addCase(updateUserDetails.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;
      
      // Update user details with the new data
      if (action.payload?.data) {
        state.userDetails = { ...state.userDetails, ...action.payload.data };
      }
    });
    builder.addCase(updateUserDetails.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
      state.isError = true;
    });

    // getUserOrganizations reducers
    builder.addCase(getUserOrganizations.pending, (state) => {
      state.isUserOrganizationsLoading = true;
      state.isUserOrganizationsError = false;
    });
    builder.addCase(getUserOrganizations.fulfilled, (state, action) => {
      state.isUserOrganizationsLoading = false;
      state.userOrganizations = action.payload;
    });
    builder.addCase(getUserOrganizations.rejected, (state, action) => {
      state.isUserOrganizationsLoading = false;
      state.userOrganizationsError = action.payload;
      state.isUserOrganizationsError = true;
    });
  },
});

export const { clearSuccess, updateSelectedUser, clearState } = userSlice.actions;
export default userSlice.reducer;
